'use client'

import { ArrowUturnLeftIcon, ArrowUturnRightIcon, ListBulletIcon } from '@heroicons/react/24/outline'
import { Button } from '@tebuto/buttons/Button'
import { useFieldErrorVisible } from '@tebuto/forms/useFieldErrorVisible'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { LeavePageGuardContext } from '@tebuto/guards/leave-page/LeavePageGuard'
import { cn } from '@tebuto/lib/utils'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TextAlign from '@tiptap/extension-text-align'
import { type ChainedCommands, type Editor, EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import DOMPurify from 'dompurify'
import { FormikValues, getIn, useFormikContext } from 'formik'
import { AlignCenter, AlignJustify, AlignLeft, AlignRight, Check, LinkIcon, Quote } from 'lucide-react'
import { type FocusEvent, type JSX, type MouseEvent, useCallback, useContext, useEffect, useState } from 'react'
import { EditorTableBubbleMenu, InsertTableButton } from './EditorTableControls'
import IndentExtension from './IndentExtension'
import { NonNestedTableCell, NonNestedTableHeader } from './NonNestedTableExtensions'
import { TableKeyboardDeletionExtension } from './TableKeyboardDeletionExtension'

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

/** Keep selection and avoid page/editor jump when applying toolbar commands. */
function runToolbarCommand(editor: Editor | null, command: (chain: ChainedCommands) => ChainedCommands): void {
    if (!editor) {
        return
    }
    command(editor.chain().focus(undefined, { scrollIntoView: false })).run()
}

function preventToolbarMouseDown(event: MouseEvent<HTMLElement>): void {
    const target = event.target
    if (!(target instanceof Element)) {
        return
    }
    // Buttons must not steal focus; selects still need mousedown to open.
    if (target.closest('button')) {
        event.preventDefault()
    }
}

interface SimpleFormikEditorProps {
    defaultValue?: string
    tabIndex?: number
    /** When omitted, autofocus follows legacy behavior: enabled when `tabIndex === 0`. */
    autoFocus?: boolean
    accessorKey: string
    htmlHeadingLevelOffSet?: 0 | 1 | 2 | 3
    /** Restrict allowed heading levels. Defaults to three levels starting at `1 + htmlHeadingLevelOffSet`. */
    headingLevels?: HeadingLevel[]
    showHeadings?: boolean
    showTextAlignButtons?: boolean
    showTables?: boolean
    showSaveButton?: boolean
    minHeight?: string
    maxHeight?: string
    border?: boolean
    leavePageGuard?: boolean
    readOnly?: boolean
    name?: string
    dataTestId?: string
    contentClassName?: string
    enableLinks?: boolean
    enableBlockquote?: boolean
}

/**
 * A React component that integrates a rich text editor with Formik.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.defaultValue=''] - The default content of the editor.
 * @param {number} [props.tabIndex=0] - The tab index for the editor, which determines the order of focus when tabbing through elements.
 * @param {string} props.accessorKey - The key used to access the form field value, typically corresponding to a field name in the form.
 * @param {number} [props.htmlHeadingLevelOffSet=0] - The offset for the heading levels (must be between 0 and 3). Default is 0. If the offset is 1, the heading levels will be h2 for heading 1, h3 for heading 2, and so on.
 * @param {function} props.onChange - Callback function that is called when the content of the editor changes.
 * @param {boolean} [props.readOnly=false] - If true, the editor is in read-only mode and cannot be edited.
 * @param {string} [props.placeholder=''] - Placeholder text displayed when the editor is empty.
 * @param {Object} [props.style={}] - Custom styles to apply to the editor.
 * @param {Object} [props.className=''] - Additional CSS classes to apply to the editor.
 *
 * @throws {Error} If `htmlHeadingLevelOffSet` is not between 0 and 3.
 *
 * @returns {JSX.Element} The rendered SimpleFormikEditor component.
 */
export function SimpleFormikEditor({
    defaultValue = '',
    tabIndex = 0,
    autoFocus,
    accessorKey,
    htmlHeadingLevelOffSet = 0,
    headingLevels,
    showHeadings = true,
    showTextAlignButtons = true,
    showTables = true,
    showSaveButton = false,
    border = true,
    minHeight,
    maxHeight = '35vh',
    leavePageGuard = false,
    readOnly = false,
    name,
    dataTestId,
    contentClassName,
    enableLinks = false,
    enableBlockquote = false
}: SimpleFormikEditorProps): JSX.Element {
    const { t } = useComponentTranslation()
    const { setFieldValue, setFieldTouched, submitForm, values, errors, isSubmitting, validateOnBlur } =
        useFormikContext<FormikValues>()
    const showFieldError = useFieldErrorVisible(accessorKey)

    const handleEditorBlur = useCallback(
        (e: FocusEvent<HTMLDivElement>) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) {
                return
            }
            setFieldTouched(accessorKey, true, validateOnBlur !== false)
        },
        [accessorKey, setFieldTouched, validateOnBlur]
    )
    const { registerLeavePageGuard, unregisterLeavePageGuard, updateDirtyState } = useContext(LeavePageGuardContext)

    const [headingLevel, setHeadingLevel] = useState(0)
    const [isDirty, setIsDirty] = useState(false)
    const [isLinkActive, setIsLinkActive] = useState(false)
    const [isBlockquoteActive, setIsBlockquoteActive] = useState(false)

    if (htmlHeadingLevelOffSet > 3 || htmlHeadingLevelOffSet < 0) {
        throw new Error('htmlHeadingLevelOffSet must be between 0 and 3')
    }

    const resolvedHeadingLevels: HeadingLevel[] = headingLevels ?? [
        (1 + htmlHeadingLevelOffSet) as HeadingLevel,
        (2 + htmlHeadingLevelOffSet) as HeadingLevel,
        (3 + htmlHeadingLevelOffSet) as HeadingLevel
    ]

    const editorContentStyle =
        minHeight || maxHeight
            ? {
                  ...(minHeight ? { minHeight } : {}),
                  ...(maxHeight ? { maxHeight } : {})
              }
            : undefined

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: resolvedHeadingLevels
                },
                codeBlock: false,
                strike: false,
                horizontalRule: false,
                gapcursor: false,
                code: false,
                blockquote: enableBlockquote ? undefined : false,
                link: enableLinks
                    ? {
                          openOnClick: false,
                          HTMLAttributes: {
                              rel: 'noopener noreferrer'
                          }
                      }
                    : false,
                dropcursor: false
            }),
            TextAlign.configure({
                alignments: ['left', 'justify', 'right', 'center'],
                types: ['heading', 'paragraph']
            }),
            IndentExtension,
            Table.configure({
                resizable: true
            }),
            TableRow,
            NonNestedTableHeader,
            NonNestedTableCell,
            TableKeyboardDeletionExtension
        ],
        content: defaultValue,
        autofocus: autoFocus ?? tabIndex === 0,
        editable: !readOnly,
        immediatelyRender: true,
        onUpdate: ({ editor }) => {
            // Client-side sanitization
            const htmlContent = DOMPurify.sanitize(editor.getHTML())
            if (htmlContent !== values[accessorKey]) {
                setFieldValue(accessorKey, htmlContent)
            }
        }
    })

    const handleDiscard = useCallback(() => {
        if (editor) {
            editor.commands.setContent(values[accessorKey])
            setIsDirty(false)
        }
    }, [editor, setIsDirty, values[accessorKey]])

    const handleSave = useCallback(async () => {
        await submitForm()
        setIsDirty(false)
        if (updateDirtyState && name) {
            updateDirtyState(name, false)
        }
    }, [submitForm, setIsDirty, updateDirtyState, name])

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return
        }

        if (values[accessorKey] !== editor.getHTML()) {
            editor.commands.setContent(values[accessorKey] || '')
        }
    }, [values[accessorKey], editor, accessorKey])

    // Sync heading level with select dropdown
    useEffect(() => {
        if (!editor) return

        const syncToolbarState = () => {
            const currentNode = editor.state.selection.$anchor.parent
            if (currentNode.type.name === 'heading') {
                setHeadingLevel(currentNode.attrs.level)
            } else {
                setHeadingLevel(0)
            }
            if (enableLinks) {
                setIsLinkActive(editor.isActive('link'))
            }
            if (enableBlockquote) {
                setIsBlockquoteActive(editor.isActive('blockquote'))
            }
        }

        const handleUpdate = () => {
            setIsDirty(true)
            if (leavePageGuard && name) {
                updateDirtyState(name, true)
            }
            syncToolbarState()
        }

        syncToolbarState()
        editor.on('selectionUpdate', syncToolbarState)
        editor.on('update', handleUpdate)
        editor.on('focus', syncToolbarState)
        editor.on('blur', syncToolbarState)

        return () => {
            editor.off('selectionUpdate', syncToolbarState)
            editor.off('update', handleUpdate)
            editor.off('focus', syncToolbarState)
            editor.off('blur', syncToolbarState)
        }
    }, [editor, name, leavePageGuard, setIsDirty, updateDirtyState, enableLinks, enableBlockquote])

    useEffect(() => {
        if (!leavePageGuard) {
            return
        }

        if (!registerLeavePageGuard) {
            throw new Error('DocumentationEditor must be a child of LeavePageGuard')
        }

        if (!name) {
            throw new Error('If the LeavePageGuard is used, the name must be provided')
        }

        registerLeavePageGuard({ name, save: handleSave, discard: handleDiscard })

        return () => unregisterLeavePageGuard(name)
    }, [leavePageGuard, name])

    useEffect(() => {
        if (!showSaveButton) return

        const handleKeyDown = (event: KeyboardEvent) => {
            const isMac = /Mac/i.test(navigator.userAgent)
            const isSaveKey = isMac ? event.metaKey && event.key === 's' : event.ctrlKey && event.key === 's'

            if (isSaveKey) {
                event.preventDefault()
                handleSave()
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [showSaveButton, handleSave])

    return (
        <div
            className="flex h-full min-w-0 max-w-full flex-col overflow-hidden"
            onBlur={readOnly ? undefined : handleEditorBlur}
        >
            {/* Toolbar with editor commands */}
            <div
                className={cn(
                    'flex min-w-0 max-w-full flex-wrap items-center gap-1 bg-gray-50/80 px-3 py-1.5 sticky top-0',
                    border ? 'border-t border-x border-gray-200 rounded-t-md' : 'border-b border-gray-100',
                    showFieldError ? ' border-red-500' : ''
                )}
                onMouseDown={preventToolbarMouseDown}
            >
                {showHeadings && (
                    <>
                        <select
                            tabIndex={-1}
                            onChange={(e) => {
                                const level = Number.parseInt(e.target.value, 10) as 0 | HeadingLevel
                                if (!editor) return

                                if (level === 0) {
                                    runToolbarCommand(editor, (chain) => chain.setNode('paragraph'))
                                } else {
                                    runToolbarCommand(editor, (chain) => chain.toggleHeading({ level }))
                                }

                                const currentNode = editor.state.selection.$anchor.parent
                                if (currentNode.type.name === 'heading') {
                                    setHeadingLevel(currentNode.attrs.level)
                                } else {
                                    setHeadingLevel(0)
                                }
                            }}
                            className="h-8 shrink-0 pt-1.5 pb-0.5 px-2.5 rounded-md text-[13px] leading-tight text-gray-600 bg-white border border-gray-200 hover:border-gray-300 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors cursor-pointer"
                            value={resolvedHeadingLevels.includes(headingLevel as HeadingLevel) ? headingLevel : 0}
                            disabled={readOnly}
                        >
                            {resolvedHeadingLevels.map((level, index) => (
                                <option key={level} value={level}>
                                    {resolvedHeadingLevels.length === 1 ? 'Überschrift' : `Überschrift ${index + 1}`}
                                </option>
                            ))}
                            <option value="0">Text</option>
                        </select>
                        <div className="w-px h-5 bg-gray-200 mx-1.5" />
                    </>
                )}

                <div className={cn('flex min-w-0 flex-wrap', readOnly ? 'opacity-50' : '')}>
                    {/* Bold */}
                    <button
                        tabIndex={-1}
                        type="button"
                        onClick={() => runToolbarCommand(editor, (chain) => chain.toggleBold())}
                        className={cn(
                            'h-8 w-8 flex items-center justify-center rounded-md text-[13px] font-semibold transition-colors',
                            editor?.isActive('bold')
                                ? 'bg-gray-200 text-gray-900'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        )}
                        disabled={readOnly}
                    >
                        B
                    </button>
                    {/* Italic */}
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => runToolbarCommand(editor, (chain) => chain.toggleItalic())}
                        className={cn(
                            'h-8 w-8 flex items-center justify-center rounded-md text-[13px] italic transition-colors',
                            editor?.isActive('italic')
                                ? 'bg-gray-200 text-gray-900'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        )}
                    >
                        I
                    </button>
                    {/* Underline */}
                    <button
                        tabIndex={-1}
                        type="button"
                        onClick={() => runToolbarCommand(editor, (chain) => chain.toggleUnderline())}
                        className={cn(
                            'h-8 w-8 flex items-center justify-center rounded-md text-[13px] underline transition-colors',
                            editor?.isActive('underline')
                                ? 'bg-gray-200 text-gray-900'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        )}
                        disabled={readOnly}
                    >
                        U
                    </button>
                </div>

                <div className="w-px h-5 bg-gray-200 mx-1.5" />

                <div className={cn('flex min-w-0 flex-wrap', readOnly ? 'opacity-50' : '')}>
                    {showTextAlignButtons && (
                        <>
                            {/* Text Left */}
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => runToolbarCommand(editor, (chain) => chain.setTextAlign('left'))}
                                className={cn(
                                    'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                    editor?.isActive({ textAlign: 'left' })
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                )}
                                disabled={readOnly}
                            >
                                <AlignLeft className="w-4 h-4" />
                            </button>
                            {/* Text Center */}
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => runToolbarCommand(editor, (chain) => chain.setTextAlign('center'))}
                                className={cn(
                                    'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                    editor?.isActive({ textAlign: 'center' })
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                )}
                                disabled={readOnly}
                            >
                                <AlignCenter className="w-4 h-4" />
                            </button>
                            {/* Text Right */}
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => runToolbarCommand(editor, (chain) => chain.setTextAlign('right'))}
                                className={cn(
                                    'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                    editor?.isActive({ textAlign: 'right' })
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                )}
                                disabled={readOnly}
                            >
                                <AlignRight className="w-4 h-4" />
                            </button>
                            {/* Text Justify */}
                            <button
                                type="button"
                                tabIndex={-1}
                                onClick={() => runToolbarCommand(editor, (chain) => chain.setTextAlign('justify'))}
                                className={cn(
                                    'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                    editor?.isActive({ textAlign: 'justify' })
                                        ? 'bg-gray-200 text-gray-900'
                                        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                                )}
                                disabled={readOnly}
                            >
                                <AlignJustify className="w-4 h-4" />
                            </button>
                        </>
                    )}
                    {/* Bullet List */}
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => runToolbarCommand(editor, (chain) => chain.toggleBulletList())}
                        className={cn(
                            'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                            editor?.isActive('bulletList')
                                ? 'bg-gray-200 text-gray-900'
                                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        )}
                        disabled={readOnly}
                    >
                        <ListBulletIcon className="w-4 h-4" />
                    </button>
                    {enableBlockquote && (
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => runToolbarCommand(editor, (chain) => chain.toggleBlockquote())}
                            className={cn(
                                'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                isBlockquoteActive
                                    ? 'bg-gray-200 text-gray-900'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            )}
                            disabled={readOnly}
                            data-testid={`${dataTestId ?? accessorKey}-blockquote`}
                        >
                            <Quote className="w-4 h-4" />
                        </button>
                    )}
                    {enableLinks && (
                        <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => {
                                if (!editor) return
                                if (editor.isActive('link')) {
                                    runToolbarCommand(editor, (chain) => chain.unsetLink())
                                    return
                                }
                                const url = window.prompt('URL')
                                if (!url) return
                                runToolbarCommand(editor, (chain) =>
                                    chain.extendMarkRange('link').setLink({ href: url })
                                )
                            }}
                            className={cn(
                                'h-8 w-8 flex items-center justify-center rounded-md transition-colors',
                                isLinkActive
                                    ? 'bg-gray-200 text-gray-900'
                                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                            )}
                            disabled={readOnly}
                            data-testid={`${dataTestId ?? accessorKey}-link`}
                        >
                            <LinkIcon className="w-4 h-4" />
                        </button>
                    )}
                    {showTables && (
                        <InsertTableButton
                            editor={editor}
                            readOnly={readOnly}
                            dataTestIdPrefix={dataTestId ?? accessorKey}
                        />
                    )}
                </div>

                <div className="w-px h-5 bg-gray-200 mx-1.5" />

                <div className={cn('flex min-w-0 flex-wrap', readOnly ? 'opacity-50' : '')}>
                    {/* Undo */}
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => runToolbarCommand(editor, (chain) => chain.undo())}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        disabled={readOnly}
                    >
                        <ArrowUturnLeftIcon className="w-4 h-4" />
                    </button>
                    {/* Redo */}
                    <button
                        type="button"
                        tabIndex={-1}
                        onClick={() => runToolbarCommand(editor, (chain) => chain.redo())}
                        className="h-8 w-8 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                        disabled={readOnly}
                    >
                        <ArrowUturnRightIcon className="w-4 h-4" />
                    </button>
                </div>

                {showSaveButton && (
                    <div className="flex items-center ml-auto">
                        <Button
                            disabled={!isDirty || readOnly}
                            outline={!isDirty}
                            onClick={handleSave}
                            layout={isDirty ? 'default' : 'ghost'}
                            isLoading={isSubmitting}
                            color={isDirty ? 'primary' : 'gray'}
                            size="sm"
                            className="h-8"
                        >
                            {isDirty ? (
                                t('save')
                            ) : (
                                <div className="flex gap-1.5 items-center text-gray-500">
                                    <Check className="size-3.5" />
                                    {t('saved')}
                                </div>
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {/* Editor Content Area */}
            <div
                className={cn(
                    'flex min-h-0 min-w-0 max-w-full flex-1 bg-white',
                    border ? 'border border-gray-200 rounded-b-md' : '',
                    showFieldError ? ' border-red-500' : ''
                )}
            >
                {showTables && (
                    <EditorTableBubbleMenu
                        editor={editor}
                        readOnly={readOnly}
                        dataTestIdPrefix={dataTestId ?? accessorKey}
                    />
                )}
                <EditorContent
                    id={accessorKey}
                    tabIndex={tabIndex}
                    onClick={() => editor?.commands.focus(undefined, { scrollIntoView: false })}
                    style={editorContentStyle}
                    className={cn(
                        'h-full overflow-auto relative cursor-text tebutoEditor px-5 py-4 flex grow flex-1 [&>div]:flex-1',
                        minHeight ? undefined : 'min-h-48',
                        contentClassName
                    )}
                    name={accessorKey}
                    spellCheck
                    editor={editor}
                    data-testid={dataTestId}
                />
            </div>
            {showFieldError && (
                <span className="text-xxs font-medium text-red-500 mt-2">{String(getIn(errors, accessorKey))}</span>
            )}
        </div>
    )
}
